import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    usePerformBoOperationMutation,
    type PerformBoOperationVariables,
} from "@src/businessModules/WorkShop/shared/bo/usePerformBoOperationMutation";
import {
    storePlacesServices,
    type StorePlaceCancelSdbPlacementParams,
    type StorePlacePlaceSdbParams,
} from "../../../../api/storePlaces";

/** Параметры размещения по SDB с кодом строки для точечного refetch. */
export type FinishedGoodsReceiptPlaceBySdbVariables = StorePlacePlaceSdbParams & {
    /** Код строки состава ВЗН УП для обновления в cache. */
    contentCode: number;
};

/** Параметры отмены размещения по SDB с кодом строки для точечного refetch. */
export type FinishedGoodsReceiptCancelPlacementBySdbVariables = StorePlaceCancelSdbPlacementParams & {
    /** Код строки состава ВЗН УП для обновления в cache. */
    contentCode: number;
};

/** Параметры хука мутаций карточки приемки СГП. */
export interface UseStockFinishedGoodsReceiptCardMutationsParams {
    /**
     * Колбэк синхронизации обновлённой строки ВЗН после размещения.
     * @param contentCode код строки состава ВЗН УП
     */
    onContentRefetch?: (contentCode: number) => void | Promise<unknown>;
    /** Колбэк успешной BO-операции. */
    onBoOperationSuccess?: () => void;
}

/** Мутации карточки приемки СГП. */
export interface UseStockFinishedGoodsReceiptCardMutationsResult {
    /** Размещение продукции в ячейку по `ArrivalSdbCode`. */
    placeBySdbMutation: UseMutationResult<void, Error, FinishedGoodsReceiptPlaceBySdbVariables>;
    /** Отмена размещения продукции по `ArrivalSdbCode`. */
    cancelPlacementBySdbMutation: UseMutationResult<void, Error, FinishedGoodsReceiptCancelPlacementBySdbVariables>;
    /** Выполнение BO-операции над ВЗН УП. */
    performBoOperationMutation: UseMutationResult<void, Error, PerformBoOperationVariables>;
}

/** Screen-level мутации карточки приемки готовой продукции на СГП. */
export function useStockFinishedGoodsReceiptCardMutations({
    onContentRefetch,
    onBoOperationSuccess,
}: UseStockFinishedGoodsReceiptCardMutationsParams): UseStockFinishedGoodsReceiptCardMutationsResult {
    const placeBySdbMutation = useMutation({
        mutationKey: ["stock", "finishedGoodsReceipt", "placeBySdb"],
        meta: { errorSource: "stock.finishedGoodsReceipt.placeBySdb" },
        mutationFn: ({ contentCode: _contentCode, ...variables }: FinishedGoodsReceiptPlaceBySdbVariables) =>
            storePlacesServices.placeBySdb(variables),
        onSuccess: async (_data, variables) => {
            notification.success("Размещение продукции сохранено");
            await onContentRefetch?.(variables.contentCode);
        },
    });

    const cancelPlacementBySdbMutation = useMutation({
        mutationKey: ["stock", "finishedGoodsReceipt", "cancelPlacementBySdb"],
        meta: { errorSource: "stock.finishedGoodsReceipt.cancelPlacementBySdb" },
        mutationFn: ({ contentCode: _contentCode, ...variables }: FinishedGoodsReceiptCancelPlacementBySdbVariables) =>
            storePlacesServices.cancelPlacementBySdb(variables),
        onSuccess: async (_data, variables) => {
            notification.success("Размещение продукции отменено");
            await onContentRefetch?.(variables.contentCode);
        },
    });

    const performBoOperationMutation = usePerformBoOperationMutation({
        scope: "stockFinishedGoodsReceipt",
        onSuccess: onBoOperationSuccess,
    });

    return {
        placeBySdbMutation,
        cancelPlacementBySdbMutation,
        performBoOperationMutation,
    };
}
